import * as clipboard from 'clipboard-polyfill'
import * as platform from 'platform'
import {Lang} from 'yougile-lang'
import {HTMLUtil, logger, ObjUtil, Time, UIUtil, Util} from 'yougile-util'
import {
  Diff,
  getNextScheduledReadable,
  getNextScheduledTime,
  GroupChat,
  HubUtil,
  isDirectlyVisible,
  scheduleToReadable,
  toLocalSchedule,
  toServerSchedule
} from 'yougile-model'
import {DevUtils} from '@client/dev-utils'
import {LazyLoadClient} from '@client/lazy-load-client'
import {mouse} from '@common/mouse'
import {TrackingEvent} from '@common/tracking-events'
import {BoardTabBg} from '@components/board/board-tab-bg'
import {
  showAlert,
  showConfirm,
  showPrompt
} from '@components/common/base/notifications'
import {getUnseenMessages} from '@extensions/chat'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {News} from '@extensions/news'
import {TaskChatProxyService} from '@extensions/proxies/task-chat-proxy'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Backend} from '../../common/backend'
import {DragClick} from '../../common/drag/drag-click'
import {DragData} from '../../common/drag-data'
import {FileChooser} from '../../common/file-chooser'
import {FilePreview} from '../../common/file-preview'
import {MonacoEditor} from '../../common/monaco-editor'
import {Notifications} from '../../common/notifications'
import {Tracking} from '../../common/tracking'
import {UserCardClient} from '../../common/user-card-client'
import {UtilUi} from '../../common/utils-ui'
import * as YougileAnalytics from '../../common/yougile-analytics'
import {Accounts} from '../client/accounts'
import {ArchiveClient} from '../client/archive-client'
import {ClientLogger} from '../client/client-logger'
import {CompanyLink} from '../client/company-link'
import {CompanyPreferencesClient} from '../client/company-preferences-client'
import {Connector, SyncFallback} from '../client/connector'
import {DepartmentsClient} from '../client/departments-client'
import {DirectMessages} from '../client/direct-messages'
import {EventBus} from '../client/event-bus'
import * as events from '../client/events'
import {ExtensionClient} from '../client/extension-client'
import {GroupChatsClient} from '../client/group-chats-client'
import {MarketplaceClient} from '../client/marketplace-client'
import {PartnerClient} from '../client/partner-client'
import {PaymentClient} from '../client/payment-client'
import {PermissionsChecker} from '../client/permissions-checker'
import {
  PermissionsClient,
  PermissionsClientHelper
} from '../client/permissions-client'
import {Refresh} from '../client/refresh'
import {RepeatClient} from '../client/repeat-client'
import {SharedBoardsClient} from '../client/shared-boards-client'
import {SharedBoardsHelper} from '../client/shared-boards-helper'
import {Storage} from '../client/storage'
import {Sync, SyncAutoSaver} from '../client/sync'
import {SyncEvents} from '../client/sync-events'
import {UserEventsClient} from '../client/user-events-client'
import {UserSettings} from '../client/user-settings'
import {Closable} from '../components/closable'
import {FileUploadProgress} from '../components/file-upload-progress'
import {MessageBox} from '../components/message-box'
import {Board} from '../extensions/board'
import {ChatMessageUtils} from '../extensions/chat-message-utils'
import {ChatSubscribers} from '../extensions/chat-subscribers'
import {ChatUtils} from '../extensions/chat-utils'
import {Column} from '../extensions/column'
import {Company} from '../extensions/company'
import {EmojiUtils} from '../extensions/emoji-utils'
import {Feedback} from '../extensions/feedback'
import {globals} from '../extensions/globals-base'
import {Mailbox, MailboxManager} from '../extensions/mailbox'
import {MessageUtils} from '../extensions/message-utils'
import {PageService} from '../extensions/page-service'
import {Project} from '../extensions/project'
import {PersonalChatProxy} from '../extensions/proxies/personal-chat-proxy'
import {UserProxy} from '../extensions/proxies/user-proxy'
import {Search} from '../extensions/search'
import {SeenBy} from '../extensions/seen-by'
import {BoardFilters} from '../extensions/sticker/board-filters'
import {DeadlineCtl} from '../extensions/sticker/controllers/deadline-ctl'
import {Sticker, stickerCtl} from '../extensions/sticker/sticker'
import {SystemEntities} from '../extensions/system-entities'
import {Task} from '../extensions/task'
import {UserAccess} from '../extensions/user-access'
import {WebPay} from '../extensions/web-pay'
import {Api} from './api'
import {ApiPopupCtl} from './components/api-popup'
import {fixDeletedTasksWithoutDeleteEvents} from './helpers/fix-deleted-tasks-without-delete-events'
import {getStickers} from './helpers/get-stickers'
import {getTrackData} from './helpers/get-track-data'
import {Notifier} from './model/notifier'
import {UIRegistry} from './model/ui-registry'
import {Runner} from './runner'

declare global {
  interface Window {
    api: typeof browserConsoleApi
    Api: Api
    CKEDITOR_TRANSLATIONS: any
  }
}

/**
 * API module is for debugging convenience.
 * It is added to the window object (init
 * method is called by the main controller).
 */
export const browserConsoleApi = {
  UIUtil,
  HTMLUtil,
  DevUtils,
  MessageUtils,
  EmojiUtils,
  ClientLogger,
  logger,
  DepartmentsClient,
  SubtaskUtil,
  CompanyPreferencesClient,
  UserCardClient,
  UserSettings,
  Task,
  ChatSubscribers,
  FilePreview,
  ChatUtils,
  Sync,
  SyncAutoSaver,
  SystemEntities,
  SeenBy,
  UserAccess,
  Lang,
  Connector,
  SyncFallback,
  Project,
  Board,
  Column,
  Sticker,
  MessageBox,
  DirectMessages,
  Backend,
  globals,
  Search,
  UtilUi,
  BoardTabBg,
  Company,
  Accounts,
  FileUploadProgress,
  Feedback,
  PageService,
  MonacoEditor,
  MarketplaceClient,
  ChatMessageUtils,
  Noty: Notifications,
  BoardFilters,
  SyncEvents,
  Tracking,
  TrackingEvent,
  Refresh,
  EventBus,
  events,
  Mailbox,
  MailboxManager,
  PersonalChatProxy,
  Runner,
  platform,
  PermissionsClient,
  PermissionsClientHelper,
  PermissionsChecker,
  PaymentClient,
  WebPay,
  UserEventsClient,
  DragClick,
  mouse,
  DragData,
  Util,
  Time,
  GroupChatsClient,
  GroupChat,
  stickerCtl,
  DeadlineCtl,
  CompanyLink,
  Closable,
  UserProxy,
  isDirectlyVisible,
  ObjUtil,
  Diff,
  RepeatClient,
  ArchiveClient,
  toLocalSchedule,
  toServerSchedule,
  scheduleToReadable,
  getNextScheduledReadable,
  getNextScheduledTime,
  PartnerClient,
  FileChooser,
  ApiPopupCtl,
  ExtensionClient,
  UIRegistry,
  SharedBoardsClient,
  SharedBoardsHelper,
  Storage,
  getUnseenMessages,
  TaskChatProxy: TaskChatProxyService,
  News,
  helpers: {
    getStickers,
    getTrackData
  },
  showAlert,
  showConfirm,
  showPrompt,
  clipboard,
  LazyLoadClient,
  HubUtil,
  idTasksApi,
  YougileAnalytics,
  Notifier,
  fixDeletedTasksWithoutDeleteEvents
}

export const initApi = (): void => {
  window.api = browserConsoleApi
  window.Api = Api
}
